"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { PublicAuthUser } from "@/lib/auth/types";
import { useAccountLocaleSync } from "@/hooks/use-account-locale-sync";
import {
  clearCachedAccessToken,
  runDedupedAccessTokenFetch,
} from "@/lib/auth/client-access-token-cache";
import {
  getAuthUserForProvider,
  getAccessToken,
  signOut as signOutAction,
} from "@/lib/auth/session.actions";

type InternalAuthContextValue = {
  readonly user: PublicAuthUser | null;
  readonly isLoaded: boolean;
  readonly isSignedIn: boolean;
  readonly refreshSession: () => Promise<PublicAuthUser | null>;
  readonly getToken: (orgId?: string) => Promise<string | null>;
  readonly signOut: () => Promise<void>;
};

const InternalAuthContext = createContext<InternalAuthContextValue | null>(
  null,
);

export const InternalAuthProvider = ({
  children,
}: {
  readonly children: ReactNode;
}) => {
  const [user, setUser] = useState<PublicAuthUser | null>(null);
  const [isLoaded, setIsLoaded] = useState<boolean>(false);
  useAccountLocaleSync(user);

  const refreshSession = useCallback(async () => {
    const nextUser = await getAuthUserForProvider();
    setUser(nextUser);
    setIsLoaded(true);
    return nextUser;
  }, []);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const nextUser = await getAuthUserForProvider();
        if (!cancelled) {
          setUser(nextUser);
        }
      } catch {
        // API fora do ar (reiniciando, deploy): fica sem usuário nesta
        // carga em vez de virar unhandledRejection. A sessão não é apagada.
      } finally {
        if (!cancelled) {
          setIsLoaded(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const getToken = useCallback(async (orgId?: string) => {
    return runDedupedAccessTokenFetch(orgId, async () => {
      const bundle = await getAccessToken(orgId);
      return {
        accessToken: bundle?.accessToken ?? null,
        expiresIn: bundle?.expiresIn ?? 60,
      };
    });
  }, []);

  const signOut = useCallback(async () => {
    clearCachedAccessToken();
    await signOutAction();
    setUser(null);
  }, []);

  const value = useMemo<InternalAuthContextValue>(
    () => ({
      user,
      isLoaded,
      isSignedIn: Boolean(user),
      refreshSession,
      getToken,
      signOut,
    }),
    [user, isLoaded, refreshSession, getToken, signOut],
  );

  return (
    <InternalAuthContext.Provider value={value}>
      {children}
    </InternalAuthContext.Provider>
  );
};

export const useInternalAuth = (): InternalAuthContextValue => {
  const ctx = useContext(InternalAuthContext);
  if (!ctx) {
    throw new Error("useInternalAuth must be used within InternalAuthProvider");
  }
  return ctx;
};
